import { useMutation } from '@tanstack/react-query'
import { syncDocumentsInterop } from '../../../data/patients'
import { DocumentInstanceListItem } from '../../../model/DocumentInstance'
import { UserFriendlyError } from '../../../model/Error'
import { CustomFile } from '../../../model/File'
import { addValid } from '../../../store/message'
import { useActionDispatch } from '../../utils'

export type InteropSyncableDocument = Pick<DocumentInstanceListItem, 'id' | 'type' | 'category'>
export type InteropSyncableFile = Pick<CustomFile, 'id'>

export interface SyncDocumentsInteropMutateVariable {
  serial: string
  medicalEventId: number
  documents: ReadonlyArray<InteropSyncableDocument>
  files: ReadonlyArray<InteropSyncableFile>
}

const SYNC_DOCUMENTS_ERROR_TITLE = "La transmission des documents à l'établissement a échoué"

export const useSyncDocumentsInterop = () => {
  const valid = useActionDispatch(addValid)

  return useMutation({
    mutationFn: async ({
      serial,
      medicalEventId,
      documents,
      files,
    }: SyncDocumentsInteropMutateVariable) => {
      if (documents.length === 0 && files.length === 0) {
        return null
      }

      const { ok, status, data } = await syncDocumentsInterop(serial, {
        medicalEventId,
        documents: documents.map(({ id, type, category }) => ({
          documentId: id,
          type,
          documentCategoryId: category,
        })),
        files: files.map(({ id }) => ({ fileId: id })),
      })

      if (!ok) {
        if (status === 403) {
          throw new UserFriendlyError(
            'error',
            SYNC_DOCUMENTS_ERROR_TITLE,
            "Vous n'êtes pas autorisé à transmettre ces documents à l'établissement",
          )
        }
        // 422 : le back renvoie un `detail` explicite (donnée manquante côté patient ou document).
        // Sur les autres statuts, `detail` porte l'erreur technique brute : elle n'a rien à faire
        // sous les yeux de l'utilisateur.
        throw new UserFriendlyError(
          'error',
          SYNC_DOCUMENTS_ERROR_TITLE,
          status === 422 && data?.detail ? data.detail : 'Veuillez réessayer ultérieurement',
        )
      }

      return documents.length + files.length
    },
    onSuccess: (sentCount) => {
      if (sentCount === null) {
        return
      }
      // The API only acknowledges the HL7 sending, not its delivery to the clinic.
      valid('Documents envoyés')
    },
  })
}
